import type { CountryOption } from '@sprd/spreadshirt-shell';
import type { ReactNode } from 'react';
// Deep imports only — the library's barrel would pull in all ~250 flags.
import AT from 'country-flag-icons/react/3x2/AT';
import AU from 'country-flag-icons/react/3x2/AU';
import BE from 'country-flag-icons/react/3x2/BE';
import CA from 'country-flag-icons/react/3x2/CA';
import CH from 'country-flag-icons/react/3x2/CH';
import DE from 'country-flag-icons/react/3x2/DE';
import DK from 'country-flag-icons/react/3x2/DK';
import ES from 'country-flag-icons/react/3x2/ES';
import FI from 'country-flag-icons/react/3x2/FI';
import FR from 'country-flag-icons/react/3x2/FR';
import GB from 'country-flag-icons/react/3x2/GB';
import IE from 'country-flag-icons/react/3x2/IE';
import IT from 'country-flag-icons/react/3x2/IT';
import NL from 'country-flag-icons/react/3x2/NL';
import NO from 'country-flag-icons/react/3x2/NO';
import PL from 'country-flag-icons/react/3x2/PL';
import SE from 'country-flag-icons/react/3x2/SE';
import US from 'country-flag-icons/react/3x2/US';
const CURRENCY_SYMBOLS: Record<string, string> = {
  AUD: 'A$',
  CAD: 'CA$',
  CHF: 'CHF',
  DKK: 'kr',
  EUR: '€',
  SEK: 'kr',
  GBP: '£',
  NOK: 'kr',
  PLN: 'zł',
  USD: '$'
};
interface ShopCountry {
  label: string;
  currency: string;
  flag: ReactNode;
}
const SHOP_COUNTRIES: Record<string, ShopCountry> = {
  'de-DE': {
    label: 'Deutschland',
    currency: 'EUR',
    flag: <DE />
  },
  'de-CH': {
    label: 'Schweiz (Deutsch)',
    currency: 'CHF',
    flag: <CH />
  },
  'de-AT': {
    label: 'Österreich',
    currency: 'EUR',
    flag: <AT />
  },
  'it-CH': {
    label: 'Svizzera (Italiano)',
    currency: 'CHF',
    flag: <CH />
  },
  'fr-CH': {
    label: 'Schweiz (Français)',
    currency: 'CHF',
    flag: <CH />
  },
  'fr-FR': {
    label: 'France',
    currency: 'EUR',
    flag: <FR />
  },
  'fr-BE': {
    label: 'Belgique (Français)',
    currency: 'EUR',
    flag: <BE />
  },
  'fi-FI': {
    label: 'Suomi',
    currency: 'EUR',
    flag: <FI />
  },
  'en-IE': {
    label: 'Ireland',
    currency: 'EUR',
    flag: <IE />
  },
  'en-GB': {
    label: 'United Kingdom',
    currency: 'GBP',
    flag: <GB />
  },
  'it-IT': {
    label: 'Italia',
    currency: 'EUR',
    flag: <IT />
  },
  'da-DK': {
    label: 'Danmark',
    currency: 'DKK',
    flag: <DK />
  },
  'nl-BE': {
    label: 'Belgie (Nederlands)',
    currency: 'EUR',
    flag: <BE />
  },
  'es-ES': {
    label: 'España',
    currency: 'EUR',
    flag: <ES />
  },
  'pl-PL': {
    label: 'Polska',
    currency: 'PLN',
    flag: <PL />
  },
  'nl-NL': {
    label: 'Nederland',
    currency: 'EUR',
    flag: <NL />
  },
  'no-NO': {
    label: 'Norge',
    currency: 'NOK',
    flag: <NO />
  },
  'sv-SE': {
    label: 'Sverige',
    currency: 'SEK',
    flag: <SE />
  },
  'en-US': {
    label: 'United States',
    currency: 'USD',
    flag: <US />
  },
  'en-CA': {
    label: 'Canada (English)',
    currency: 'CAD',
    flag: <CA />
  },
  'fr-CA': {
    label: 'Canada (Français)',
    currency: 'CAD',
    flag: <CA />
  },
  'en-AU': {
    label: 'Australia',
    currency: 'AUD',
    flag: <AU />
  }
};
const describeCurrency = (currency: string) => [CURRENCY_SYMBOLS[currency], currency].filter(Boolean).join(' ');
const toCountryOption = (locale: string, {
  label,
  currency,
  flag
}: ShopCountry): CountryOption => ({
  id: locale,
  label,
  description: describeCurrency(currency),
  flag
});

/** Ordered by country code, as the switcher has always been. */
export const COUNTRY_OPTIONS: CountryOption[] = Object.entries(SHOP_COUNTRIES).sort(([a], [b]) => (a.split('-')[1] ?? '').localeCompare(b.split('-')[1] ?? '')).map(([locale, country]) => toCountryOption(locale, country));
export const countryOptionFor = (locale: string): CountryOption => COUNTRY_OPTIONS.find(country => country.id === locale)!;
export const countryLabel = (locale: string) => SHOP_COUNTRIES[locale]?.label ?? '';